<script setup lang="ts">
import { PropType } from 'vue'
import { addProductToBasket } from '~/utils/addProductToBasket'
import { useBasketStore } from '~/stores/basket'
import ProductCodeSale from '~/models/Product/ProductCodeSale'
import FullProduct from '~/models/Product/FullProduct'
import Countdown from '~/components/elements/Countdown.vue'

const props = defineProps({
    product: {
        type: Object as PropType<FullProduct>,
        required: true,
    },
    productCode: {
        type: Object as PropType<ProductCodeSale>,
        required: true,
    },
})

const addProduct = () => {
    const basketStore = useBasketStore()
    const basketAmount = basketStore.productAmounts[props.product.id] || 0

    addProductToBasket({
        fullProduct: props.product,
        productId: props.product.id,
        amount: basketAmount + 1,
        showPreBasket: true,
        forceShowPreBasket: true,
    })
}
</script>

<template>
    <div class="price-with-code__container">
        <div class="price-with-code__wrapper">
            <div class="price-with-code__description">
                <div class="price-with-code__price notranslate" v-html="$formatCurrency(productCode.salePrice)"></div>
                <div
                    class="price-with-code__code"
                    v-html="$t('productDetail.priceWithCode.withCode', { code: productCode.voucherCode })"
                ></div>
            </div>
            <a
                v-if="!productCode.isForWeb && productCode.isForMobile"
                :href="$t('url.mobileApplication')"
                class="btn btn-primary price-with-code__button"
            >
                <span>{{ $t('productDetail.priceWithCode.buyInApp') }}</span>
            </a>
            <div v-else class="price-with-code__button_wrapper">
                <button class="btn btn-primary price-with-code__button" @click="addProduct">
                    {{ $t('productDetail.priceWithCode.buy') }}
                </button>
            </div>
        </div>
        <div v-if="productCode.showCountdown" class="price-with-code__countdown-wrapper">
            <div class="price-with-code__countdown-wrapper__title">{{ $t('dailyOffer.end') }}</div>
            <Countdown />
        </div>
        <div v-if="productCode.infoMessage" class="price-with-code__info-message" v-html="productCode.infoMessage"></div>
        <div v-if="productCode.showCountdown" class="price-with-code__tag">
            {{ $t('productDetail.priceWithCode.continuedTag') }}
        </div>
    </div>
</template>

<style scoped lang="scss">
@import '../../node_modules/bootstrap/scss/functions';
@import '../../node_modules/bootstrap/scss/variables';
@import '../../node_modules/bootstrap/scss/mixins';

.price-with-code {
    &__container {
        font-size: 13px;
        border-radius: 10px;
        margin-top: 5px;
        margin-bottom: 30px;
        line-height: 25px;
        background: $color-cta-header-light;
        padding: 15px 1em;
        position: relative;

        @include media-breakpoint-up(md) {
            align-items: stretch;
        }
    }

    &__info-message {
        margin: 0 0 20px;
        line-height: 22px;
        font-size: 13px;
    }

    &__wrapper {
        display: flex;
        justify-content: space-between;
    }

    &__button_wrapper {
        flex: none;
    }

    &__button {
        border-radius: 100px;
        padding: 8px 25px 8px 25px;
        max-height: 40px;
    }

    &__description {
        font-size: 14px;
        font-weight: 600;
        display: inline-block;

        .info-ico {
            margin-left: 8px;
            width: 12px;
            height: 12px;
            display: inline-block;
            background: url(/nuxt-assets/icons/info-grey.svg) no-repeat 50%;
            background-size: 100% 100%;
        }
    }

    &__price {
        color: $color-cta;
        font-size: 26px;
    }

    &__code {
        line-height: 16px;
        margin-top: 6px;
    }

    &__countdown-wrapper {
        margin-top: 20px;
        margin-bottom: 20px;
        display: flex;

        &__title {
            font-weight: 600;
            font-size: 14px;
            margin: auto 0;

            @media (max-width: 768px) {
                width: 35%;
            }
        }

        .countdown {
            font-size: 22px;
            color: $color-cta;
            white-space: nowrap;
            font-weight: 700;
            margin-left: auto;

            @media (max-width: 768px) {
                margin-top: 8px;
            }
        }
    }

    &__tag {
        border-radius: 100px;
        font-weight: 600;
        padding: 5px 12px 5px 12px;
        background: #ffca63;
        display: inline-block;
        position: absolute;
        bottom: -15px;
    }
}
</style>

<style>
.time::after,
.time::before {
    background-color: #ffffff !important;
}
</style>
